import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Chip, TextInput } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { catcat as cc } from "cerulean";
import {
  useGetTemplateVersions,
  useGetBulkTemplates,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { FILTER_CONFIG, FilterChips, FilterType } from "./filterConfig";
import { ActionBar } from "./common";
import styles from "./Filters.module.scss";

type TemplateWithVersions = {
  id: API.BulkTemplateId;
  name: string;
  versions: API.BulkTemplateVersion[];
  versionsLoaded: boolean;
};

/**
 * unlike other filters that use the shared FilterChip/Popover pattern, this filter
 * uses a manually positioned dropdown div. NDS Popover re-positions itself when
 * content height changes (e.g. version checkboxes expanding), which causes scroll
 * jumps in the scrollable list. a plain positioned div avoids this issue.
 *
 * versions are fetched lazily per-template on first selection and cached locally.
 *
 * URL query param support:
 *   ?template=<group_uuid> — the only supported URL param for this filter.
 *     fetches the template's versions and pre-selects all checkboxes.
 *   ?template_version is NOT supported as a URL param. version-level filtering
 *     is handled internally via the filter reducer and API query string.
 */
const TemplateFilter = ({
  filters,
  isActive,
  handleSetActive,
  handleSetFilter,
  handleToggleFilter,
}: FilterType) => {
  const { l10n } = useLocalization();
  const label = l10n.getString(FILTER_CONFIG[FilterChips.TEMPLATE].labelKey);
  const config = FILTER_CONFIG[FilterChips.TEMPLATE];
  const { queryKey } = config;

  const [searchQuery, setSearchQuery] = useState("");
  const [templates, setTemplates] = useState<TemplateWithVersions[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // when ?template= is active, use local selectedVersions (set by the group
  // fetch effect) since filters.template_version will be empty
  const selectedCount = useMemo(() => {
    const versionIds = filters.template
      ? selectedVersions
      : filters[queryKey] ?? [];
    const templatesWithSelections = templates.filter(
      (t) =>
        t.versionsLoaded && t.versions.some((v) => versionIds.includes(v.id))
    );
    return templatesWithSelections.length || undefined;
  }, [templates, filters, queryKey, selectedVersions]);

  const { getBulkTemplates } = useGetBulkTemplates();
  const { getTemplateVersions } = useGetTemplateVersions();

  // fetch all templates on mount
  useEffect(() => {
    getBulkTemplates({
      onData: (results) => {
        setTemplates(
          results.map((t) => ({
            id: t.id,
            name: t.name,
            versions: [],
            versionsLoaded: false,
          }))
        );
      },
    });
  }, [getBulkTemplates]);

  // when template group UUID is in URL, fetch its versions and select all for UI
  useEffect(() => {
    const groupId = filters.template;
    if (!groupId || templates.length === 0) {
      return;
    }
    const target = templates.find((t) => t.id === groupId);
    if (!target || target.versionsLoaded) {
      return;
    }
    getTemplateVersions(target.id, {
      onData: (versions) => {
        setTemplates((prev) =>
          prev.map((pt) =>
            pt.id === target.id ? { ...pt, versions, versionsLoaded: true } : pt
          )
        );
        setSelectedVersions(versions.map((v) => v.id));
      },
    });
  }, [templates, filters, getTemplateVersions]);

  // sync selected state from filters when dropdown opens.
  // skip when ?template= is active — selectedVersions is managed by the group
  // fetch effect above and filters[queryKey] would be empty.
  useEffect(() => {
    if (!filters.template) {
      const initialSelected = filters[queryKey] ?? [];
      setSelectedVersions(initialSelected);
    }
    if (!isActive) {
      setSearchQuery("");
    }
  }, [filters, queryKey, isActive]);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        handleSetActive("");
      }
    };
    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActive, handleSetActive]);

  const isTemplateSelected = (templateId: API.BulkTemplateId) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template?.versionsLoaded || template.versions.length === 0) {
      return false;
    }
    return template.versions.some((v) => selectedVersions.includes(v.id));
  };

  const isTemplateFullySelected = (templateId: API.BulkTemplateId) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template?.versionsLoaded || template.versions.length === 0) {
      return false;
    }
    return template.versions.every((v) => selectedVersions.includes(v.id));
  };

  const handleToggleTemplate = useCallback(
    (templateId: API.BulkTemplateId) => {
      const template = templates.find((t) => t.id === templateId);

      if (!template?.versionsLoaded) {
        // fetch versions first, then select all once loaded
        getTemplateVersions(templateId, {
          onData: (versions) => {
            setTemplates((prev) =>
              prev.map((t) =>
                t.id === templateId
                  ? { ...t, versions, versionsLoaded: true }
                  : t
              )
            );
            const versionIds = versions.map((v) => v.id);
            setSelectedVersions((prev) => [
              ...prev.filter(
                (id) => !versionIds.includes(id as API.BulkTemplateVersionId)
              ),
              ...versionIds,
            ]);
          },
        });
        return;
      }

      const versionIds = template.versions.map((v) => v.id);
      const allSelected = versionIds.every((id) =>
        selectedVersions.includes(id)
      );

      if (allSelected) {
        setSelectedVersions((prev) =>
          prev.filter(
            (id) => !versionIds.includes(id as API.BulkTemplateVersionId)
          )
        );
      } else {
        setSelectedVersions((prev) => [
          ...prev.filter(
            (id) => !versionIds.includes(id as API.BulkTemplateVersionId)
          ),
          ...versionIds,
        ]);
      }
    },
    [templates, selectedVersions, getTemplateVersions]
  );

  const handleToggleVersion = useCallback((versionId: string) => {
    setSelectedVersions((prev) =>
      prev.includes(versionId)
        ? prev.filter((id) => id !== versionId)
        : [...prev, versionId]
    );
  }, []);

  const filteredTemplates = useMemo(
    () =>
      templates.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [templates, searchQuery]
  );

  const handleToggleChip = () => {
    handleSetActive(isActive ? "" : FilterChips.TEMPLATE);
  };

  const handleApplyFilter = () => {
    // clear template from reducer so the API only sends template_version,
    // avoiding an AND conflict when multiple templates are selected.
    // template stays in the URL (via handleSetFilter skip) to keep the chip alive.
    handleSetFilter({
      ...filters,
      [queryKey]: selectedVersions,
      template: undefined,
    });
    handleToggleChip();
  };

  const handleRemoveFilter = () => {
    handleToggleFilter(FilterChips.TEMPLATE);
  };

  const renderDropdown = () => (
    <>
      <div className={styles.filterDropdownContent}>
        <TextInput
          startIcon="search"
          placeholder={l10n.getString("label-search")}
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          showClearButton={true}
        />
      </div>
      <div className={styles.filterDropdownContentScrollable}>
        {filteredTemplates.map((template) => {
          const selected = isTemplateSelected(template.id);
          const fullySelected = isTemplateFullySelected(template.id);

          return (
            <div key={template.id}>
              <div className="padding--all--xs">
                <Checkbox
                  label={template.name}
                  name={template.id}
                  checked={fullySelected}
                  indeterminate={selected && !fullySelected}
                  onChange={() => handleToggleTemplate(template.id)}
                />
              </div>
              {selected &&
                template.versionsLoaded &&
                template.versions.map((version) => (
                  <div
                    key={version.id}
                    className="padding--left--l padding--y--xs padding--right--xs"
                  >
                    <Checkbox
                      label={`V${version.version_number}`}
                      name={version.id}
                      checked={selectedVersions.includes(version.id)}
                      onChange={() => handleToggleVersion(version.id)}
                    />
                  </div>
                ))}
            </div>
          );
        })}
      </div>
      <ActionBar
        handleApplyFilter={handleApplyFilter}
        handleRemoveFilter={handleRemoveFilter}
      />
    </>
  );

  return (
    <div
      ref={wrapperRef}
      className={cc(
        styles.filterChipText,
        styles.templateFilterWrapper,
        "margin--right--m"
      )}
    >
      <Chip
        count={selectedCount}
        kind="secondary"
        label={label}
        onClick={handleToggleChip}
        endIcon={`chevron-${isActive ? "up" : "down"}`}
      />
      {isActive && (
        <div className={styles.templateFilterDropdown}>{renderDropdown()}</div>
      )}
    </div>
  );
};

export default TemplateFilter;
